<script>
import Layout from '@layouts/default'
import { customerMethods } from '@state/helpers'
import { required, sameAs } from 'vuelidate/lib/validators'
import { passwordValidator } from '@utils/validators'

export default {
  page: {
    title: 'Update Forgotten Password | AutoCheck.com',
    meta: [
      { name: 'description', content: 'Update Update Forgotten Password' },
    ],
  },
  components: { Layout },
  data() {
    return {
      newPassword: '',
      confirmPassword: '',
      updateError: null,
      updateSuccess: null,
      wasValidated: null,
      tryingToUpdate: false,
      token: null,
    }
  },
  validations: {
    newPassword: {
      required,
      passwordMatches: sameAs('confirmPassword'),
      passwordValidator,
    },
    confirmPassword: {
      required,
      passwordMatches: sameAs('newPassword'),
      passwordValidator,
    },
  },
  methods: {
    ...customerMethods,
    // Try to log the user in with the username
    // and password they provided.
    tryToUpdate() {
      // Reset the updateError and wasUnchanged message if it existed.
      this.updateError = null
      this.updateSuccess = null
      this.$v.$touch()
      this.token = this.$route.query.token
      this.email = this.$route.query.email
      this.accountunlock = this.$route.query.accountunlock

      if (this.$v.$invalid) {
        this.wasValidated = true
      } else if (!this.token || !this.email) {
        this.$router.push({
          name: 'login',
          query: {
            errorMessage:
              'The link used to access the update page was invalid.',
          },
        })
      } else {
        this.tryingToUpdate = true
        // check if tyhe account is locked and reset locked account password
        if (this.accountunlock) {
          return this.resetLockedAccountPassword({
            password: this.newPassword,
            email: this.email,
            token: this.token,
          })
            .then((response) => {
              this.tryingToUpdate = false
              // Redirect to the originally requested page, or to the home page
              this.$router.push({
                name: 'login',
                query: {
                  successMessage: 'Password successfully updated',
                },
              })
            })
            .catch((error) => {
              this.tryingToUpdate = false
              this.updateError = error
              this.$router.push({
                name: 'login',
                query: {
                  errorMessage:
                    'The link used to access the update page was invalid.',
                },
              })
            })
        }
        // reset the password using forgot password link
        else {
          return this.resetPassword({
            password: this.newPassword,
            email: this.email,
            token: this.token,
          })
            .then((response) => {
              this.tryingToUpdate = false
              // Redirect to the originally requested page, or to the home page
              this.$router.push({
                name: 'login',
                query: {
                  successMessage: 'Password successfully updated',
                },
              })
            })
            .catch((error) => {
              this.tryingToUpdate = false
              this.updateError = error
              this.$router.push({
                name: 'login',
                query: {
                  errorMessage:
                    'The link used to access the update page was invalid.',
                },
              })
            })
        }
      }
    },
  },
}
</script>

<template>
  <Layout>
    <div class="row mt-4">
      <div class="col-12 mb-4"><h1>Create your new password now.</h1></div>
      <div class="col-12">
        <div class="cardContainer cardShadow">
          <div class="cardContent">
            <b-alert v-if="updateSuccess" variant="success" show dismissible>
              {{ updateSuccess }}
            </b-alert>
            <div class="row">
              <div class="col-md-4 col-12">
                <form
                  :class="{ 'was-validated ': wasValidated }"
                  class="needs-validation"
                  novalidate
                  @submit.prevent="tryToUpdate"
                >
                  <BaseInput
                    v-model="$v.newPassword.$model"
                    name="newPassword"
                    type="password"
                    label="Create New Password"
                    :has-error="$v.newPassword.$error && wasValidated"
                  >
                    <div
                      v-if="!$v.newPassword.required"
                      class="invalid-feedback"
                      >Field is required</div
                    >
                    <div
                      v-if="!$v.newPassword.passwordValidator"
                      class="invalid-feedback"
                      >Your password must follow the password creation rules.
                      Please check your password and try again.</div
                    >
                  </BaseInput>
                  <BaseInput
                    v-model="$v.confirmPassword.$model"
                    name="confirmPassword"
                    type="password"
                    label="Enter New Password Again"
                    :has-error="$v.confirmPassword.$error && wasValidated"
                  >
                    <div
                      v-if="!$v.confirmPassword.required"
                      class="invalid-feedback"
                      >Field is required</div
                    >
                    <div
                      v-if="!$v.confirmPassword.passwordMatches"
                      class="invalid-feedback"
                      >Please enter the same password again</div
                    >
                    <div
                      v-if="!$v.confirmPassword.passwordValidator"
                      class="invalid-feedback"
                      >Your password must follow the password creation rules.
                      Please check your password and try again.</div
                    >
                  </BaseInput>
                  <BaseButton :disabled="tryingToUpdate" type="submit">
                    <BaseIcon v-if="tryingToUpdate" name="sync" spin />
                    <span v-else>
                      Create New Password
                    </span>
                  </BaseButton>
                </form>
              </div>
              <div class="col-md-4 col-12">
                <div :class="$style.passwordRules">
                  <p class="mb-0"
                    ><strong
                      >Your password must contain the following:</strong
                    ></p
                  >
                  <ul>
                    <li>At least eight (8) characters</li>
                    <li
                      >A mix of CAPITAL [A-Z] and lower case [abc] letters,
                      and</li
                    >
                    <li
                      >At least one number [123] or special character (& !
                      @)</li
                    >
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Layout>
</template>

<style lang="scss" module>
@import '@design';
.passwordRules {
  margin-top: 28px;
  font-size: 13px;
  ul {
    padding-left: 15px;
    font-style: italic;
  }
}
</style>
